// ─────────────────────────────────────────────────────────────────────────────
// branding.js  —  /promo/:branding  (e.g. /promo/cup, /promo/ad-cup, /promo/facebook, /promo/instagram …)
//
// Namespaced under /promo/ so this dynamic segment can never collide with a
// real app route (/listings, /about, etc.) — safe to add new campaign slugs
// anytime without touching routing code.
//
// Route it in App.js:
//   <Route path="/promo/:branding" element={<Branding />} />
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useRef, useState, useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import {
  QrCode, ShieldCheck, Sparkles, Heart,
  Search, MessagesSquare, KeyRound,
  BadgePercent, MessageCircle, IndianRupee, ArrowRight,
} from "lucide-react";
import { getListingLandingPageForTenant } from '../apis/services/pgApi';
import { buildSlugPath } from '../utils/slugutils';
import { useAuth } from '../context/authcontext';
import { useUI } from '../context/uicontext';
// import SEO from "./SEO";

// const BRANDING_COPY = {
//   "ad-cup": [
//     { badge: "You scanned our chai cup ☕", line1: "Drinking tea?", line2: "Why not book a PG :)" },
//     { badge: "Straight off the cup ☕", line1: "Chai done. Kya plan?", line2: "Let's find your room :)" },
//     { badge: "Cup pe milte hain ☕", line1: "Ek cutting, ek kamra?", line2: "Both sorted here :)" },
//   ],
//   facebook: [
//     { badge: "You came from Facebook 👋", line1: "Scrolling the feed?", line2: "Your next PG is right here :)" },
//     { badge: "Hello, Facebook friend 👋", line1: "Done liking posts?", line2: "Now like a room :)" },
//     { badge: "Straight from the timeline 👋", line1: "Feed refreshed?", line2: "Time to refresh your stay :)" },
//   ],
//   instagram: [
//     { badge: "You came from Instagram 📸", line1: "Done scrolling reels?", line2: "Let's find your room :)" },
//     { badge: "Swiped in from the gram 📸", line1: "Saved 40 reels today?", line2: "Save a PG instead :)" },
//     { badge: "Straight from our story 📸", line1: "Story dekh li?", line2: "Ab ghar bhi dekh lo :)" },
//   ],
// };

// ─── 1. Quirky copy per branding surface ─────────────────────────────────────
const BRANDING_COPY = {
  "ad-cup": [
    { badge: "You Scanned Right ☕", line1: "Chai ki chuski,", line2: "PG ki khushkhabri🔑" },
    { badge: "Fresh From Your Chai Cup ☕", line1: "Chai garam,", line2: " PG confirm 🔑" },
    { badge: "Straight off the cup ☕", line1: "Chai Se Refresh", line2: "PG Search Fresh 🔑" },
  ],
  "auto": [
    { badge: "Spotted us on the road 🛺", line1: "Agla Stop?", line2: "Perfect PG 🔑" },
    { badge: "Stuck in traffic? 🛺", line1: "Safar Khatam?,", line2: " Stay Shuru!🔑" },
    { badge: "Fresh Off the Ride 🛺", line1: "Ride Se Seedha", line2: "Right PG Tak 🔑" },
  ],
  "facebook": [
    { badge: "You came from Facebook 👋", line1: "Feed se seedha yahan?", line2: "Good move. Let's find your room 🔑" },
    { badge: "Hello, Facebook friend 👋", line1: "Scroll break le lo,", line2: "yeh wala scroll kaam ka hai 🔑" },
    { badge: "Straight from the timeline 👋", line1: "Timeline check ho gaya?", line2: "Ab apna next stop dekho 🔑" },
  ],
  "instagram": [
    { badge: "! Reel Deal 📸", line1: "From Reel to🤔", line2: "Real Stay 🔑" },
    { badge: "Insta Fam Exclusive📸", line1: "Reels Dekh Li...?", line2: "Ab Real PG Dekh Lo🏠🔑" },
    { badge: "You Made It From Insta 📸", line1: "Stop Scrolling.", line2: "Start Settling. 🔑" },
  ],
};

// Fallback for any unknown surface
const DEFAULT_COPY = [
  { badge: "You scanned an AapkaPG code ✨", line1: "Scanned our code?", line2: "Let's find you a PG :)" },
  { badge: "Welcome to AapkaPG ✨", line1: "Nice scan!", line2: "Now pick your room :)" },
  { badge: "You found us ✨", line1: "Looking for a stay?", line2: "You're in the right place :)" },
];

const RESERVED = ["branding", "home", "listings", "about", "contact", "blog", "pg"];

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ─── 2. Helper: Create safe slug ─────────────────────────────────────────────
const createSafeSlug = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// ─── 3. Fetch PGs for a location ─────────────────────────────────────────────
const fetchPGsForLocation = async (location) => {
  try {
    const payload = {
      SearchText: location,
      PGRentRange: null,
      PGType: null,
      PinCode: null,
    };

    console.log(`📡 Fetching PGs for location: ${location}`, payload);
    const response = await getListingLandingPageForTenant(payload);

    let pgData = [];
    if (response && Array.isArray(response)) pgData = response;
    else if (response?.data) pgData = response.data;
    else if (response?.recordsets?.[0]) pgData = response.recordsets[0];

    // Return up to 15 PGs
    return pgData.slice(0, 15);
  } catch (error) {
    console.error(`Error fetching PGs for ${location}:`, error);
    return [];
  }
};

// ─── 4. PG Card Component (simplified version - only image, name, price) ────
const BrandPGCard = ({ pg }) => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);

  const getImageUrl = () => {
    if (pg.PGPhoto && pg.PGPhoto !== 'null' && pg.PGPhoto !== '') return pg.PGPhoto;
    return null;
  };

  const rentAmount = parseFloat(pg.PGRent) || 0;

  // Randomly assign guest favourite tag (for demo - can be removed later)
  const isGuestFavourite = pg.isVerified && Math.random() > 0.7;

  const handleClick = () => {
    const slug = createSafeSlug(pg.PGName);
    navigate(`/pg/${slug}-${pg.PGID}`, { state: { PGNAME: pg.PGName, PGID: pg.PGID } });
  };

  const handleSave = (e) => {
    e.stopPropagation();
    setSaved(!saved);
  };

  return (
    <article className="brand-stay-card" onClick={handleClick}>
      <div className="brand-stay-card__media">
        <img 
          src={getImageUrl() || "/assets/placeholder-image.jpg"} 
          alt={pg.PGName} 
          loading="lazy" 
        />
        
        {/* Verified badge */}
        {pg.isVerified && (
          <span className="brand-stay-card__verified">
            <img className="verified-badge" src="/assets/verified-badge.png" alt="" />
          </span>
        )}
        
      </div>
      
      <h3 className="brand-stay-card__title">{pg.PGName}</h3>
      <p className="brand-stay-card__price">
        {pg.PGRent ? (
          <>₹{rentAmount.toLocaleString("en-IN")}<span className="brand-stay-card__price-unit">/month</span></>
        ) : (
          'Contact for rent'
        )}
      </p>
    </article>
  );
};

// ─── 5. Stay Row Component ──────────────────────────────────────────────────
const StayRow = ({ location, title, area }) => {
  const rowRef = useRef(null);
  const navigate = useNavigate();
  const [pgs, setPgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPGs = async () => {
      setLoading(true);
      setError(null);
      try {
        const results = await fetchPGsForLocation(location);
        setPgs(results);
        if (results.length === 0) {
          setError('No PGs found in this area');
        }
      } catch (err) {
        console.error('Failed to load PGs:', err);
        setError('Failed to load PGs');
      } finally {
        setLoading(false);
      }
    };

    loadPGs();
  }, [location]);

  const scrollBy = (dir) =>
    rowRef.current?.scrollBy({
      left: dir * rowRef.current.clientWidth * 0.8,
      behavior: "smooth",
    });

  const handleTitleClick = () => {
    const slugPath = buildSlugPath(location, '', '', '');
    navigate(slugPath);
  };

  // Loading state
  if (loading) {
    return (
      <section className="brand-stays">
        <div className="brand-stays__header">
          <button type="button" className="brand-stays__title" onClick={handleTitleClick}>
            <span>{title}</span>
            <span className="brand-stays__title-arrow"><ArrowRight /></span>
          </button>
        </div>
        <div className="brand-stays__loading">
          <div className="brand-stays__loading-spinner"></div>
          <p>Finding PGs in {area}...</p>
        </div>
      </section>
    );
  }

  // Error / Empty state
  if (error || pgs.length === 0) {
    return (
      <section className="brand-stays">
        <div className="brand-stays__header">
          <button type="button" className="brand-stays__title" onClick={handleTitleClick}>
            <span>{title}</span>
            <span className="brand-stays__title-arrow"><ArrowRight /></span>
          </button>
        </div>
        <div className="brand-stays__empty">
          <p>No PGs currently available in {area}.</p>
          <button className="brand-stays__empty-btn" onClick={handleTitleClick}>
            Browse all PGs in {area}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="brand-stays">
      <div className="brand-stays__header">
        <button type="button" className="brand-stays__title" onClick={handleTitleClick}>
          <span>{title}</span>
          <span className="brand-stays__title-arrow"><ArrowRight /></span>
        </button>

        <div className="brand-stays__nav">
          <button type="button" aria-label="Scroll left" onClick={() => scrollBy(-1)}>‹</button>
          <button type="button" aria-label="Scroll right" onClick={() => scrollBy(1)}>›</button>
        </div>
      </div>

      <div className="brand-stays__row" ref={rowRef}>
        {pgs.map((pg) => (
          <BrandPGCard key={pg.PGID} pg={pg} />
        ))}
      </div>
    </section>
  );
};

// ─── 6. Hero ──────────────────────────────────────────────────────────────────
function BrandingHero({ copy }) {
  return (
    <section className="brand-hero">
      <span className="brand-hero__blob brand-hero__blob--tr" />
      <span className="brand-hero__blob brand-hero__blob--bl" />

      <div className="brand-hero__inner">
        <div className="brand-hero__badge">
          <QrCode className="brand-hero__badge-icon" />
          {copy.badge}
        </div>

        <h1 className="brand-hero__title">
          {copy.line1}
          <br />
          <span className="brand-hero__title-accent">{copy.line2}</span>
        </h1>

        <p className="brand-hero__desc">
          Find Verified PGs Near You Without the Hassle. Affordable, safe, and 100% no-broker
          stays. Browse real photos, connect with owners, and book in minutes.
        </p>

        <div className="brand-hero__stats">
          <span className="brand-hero__stat">
            <ShieldCheck className="brand-hero__stat-icon" />
            12,500+ Verified PGs
          </span>
          <span className="brand-hero__stat">
            <Sparkles className="brand-hero__stat-icon" />
            Zero brokerage
          </span>
        </div>
      </div>
    </section>
  );
}

// ─── 7. Blocks copied from home.js ────────────────────────────────────────────
const WHY_ITEMS = [
  { Icon: ShieldCheck,   title: "Verified Listings",     desc: "Every PG personally verified by our team for safety & accuracy." },
  { Icon: BadgePercent,  title: "Zero Brokerage",        desc: "No middlemen, no hidden fees. What you see is what you pay." },
  { Icon: MessageCircle, title: "Instant Owner Connect", desc: "Reach out to verified owners directly — schedule visits in seconds." },
  { Icon: IndianRupee,   title: "Best Price Guarantee",  desc: "Find a lower price elsewhere? We'll match it, every time." },
];

const HOW_STEPS = [
  { n: 1, Icon: Search,         title: "Search PG",             desc: "Filter by location, budget, gender & amenities." },
  { n: 2, Icon: MessagesSquare, title: "Chat / Schedule Visit", desc: "Connect with owners and schedule a visit instantly." },
  { n: 3, Icon: KeyRound,       title: "Book Instantly",        desc: "Pay token amount online and move in." },
];

function WhyChoose() {
  return (
    <section className="apg-section">
      <div className="apg-container">
        <div className="apg-center-block">
          <div className="apg-eyebrow--plain apg-eyebrow--center">Why AapkaPG</div>
          <h2 className="apg-section-title">A renting experience you'll actually enjoy</h2>
          <p className="apg-section-sub apg-section-sub--center">Designed around real renters — not brokers.</p>
        </div>
        <div className="apg-why-grid">
          {WHY_ITEMS.map((it) => (
            <div key={it.title} className="apg-why-card">
              <div className="apg-why-card__icon"><it.Icon className="apg-why-icon" /></div>
              <h3 className="apg-why-card__title">{it.title}</h3>
              <p className="apg-why-card__desc">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="apg-section apg-section--cream">
      <div className="apg-container">
        <div className="apg-center-block">
          <div className="apg-eyebrow--plain apg-eyebrow--center">How it works</div>
          <h2 className="apg-section-title">Get a PG in 3 simple steps</h2>
        </div>
        <div className="apg-steps">
          <div className="apg-steps__line" />
          {HOW_STEPS.map((s) => (
            <div key={s.n} className="apg-step">
              <div className="apg-step__circle">
                <s.Icon className="apg-step-icon" />
                <span className="apg-step__num">{s.n}</span>
              </div>
              <h3 className="apg-step__title">{s.title}</h3>
              <p className="apg-step__desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OwnerBanner() {
  const { setShowAuth, showOwnerAlert, setShowOwnerAlert } = useUI();
  const { user } = useAuth();
  const navigate = useNavigate();

    const handleListPGClick = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    // Check user type instead of role
    if (user.type === 'pg') {
      navigate('/owner-dashboard');
    } else {
      setShowOwnerAlert(true);
    }
  };

  return (
    <>
      <section id="owners" className="apg-section" style={{padding: '100px 0'}}>
        <div className="apg-container">
          <div className="apg-owner-banner">
            <div className="apg-owner-banner__deco" />
            <div className="apg-owner-banner__glow" />
            <div className="apg-owner-banner__grid">
              <div>
                <div className="apg-badge apg-badge--glass">
                  <Sparkles className="apg-badge-icon" /> For PG Owners
                </div>
                <h2 className="apg-owner-banner__title">
                  Are You a <span className="apg-text-primary">PG Owner?</span>
                </h2>
                <p className="apg-owner-banner__desc">
                  List your property in 5 minutes and reach 50,000+ verified tenants searching every
                  month. Free to start, no commission.
                </p>
                <div className="apg-owner-banner__perks">
                  <span>✓ Free listing</span><span>✓ Verified tenants only</span><span>✓ Dashboard insights</span>
                </div>
              </div>
              <div className="apg-owner-banner__cta-wrap">
                <button
                  className="apg-btn apg-btn--primary apg-btn--lg apg-btn--rx apg-btn--glow"
                  onClick={handleListPGClick}
                >
                  List Your PG <ArrowRight className="apg-btn-icon" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {showOwnerAlert && (
        <div className="apg-modal-overlay" onClick={() => setShowOwnerAlert(false)}>
          <div className="apg-modal" onClick={(e) => e.stopPropagation()}>
            <button className="apg-modal__close" onClick={() => setShowOwnerAlert(false)}>✕</button>
            <div className="apg-modal__icon">🏠</div>
            <h3 className="apg-modal__title">Owner Access Required</h3>
            <p className="apg-modal__desc">
              {user ? "You're signed in as a tenant. Register as an owner to list your PG." : 'Register as an owner to list your PG.'}
            </p>
            <div className="apg-modal__actions">
              <button 
                className="apg-modal__btn apg-modal__btn--primary" 
                onClick={() => { 
                  setShowOwnerAlert(false); 
                  setShowAuth(true); 
                }}
              >
                Register as Owner
              </button>
              <button 
                className="apg-modal__btn apg-modal__btn--secondary" 
                onClick={() => setShowOwnerAlert(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── 8. Page ──────────────────────────────────────────────────────────────────
export default function Branding() {
  const { branding = "" } = useParams();
  const key = branding.toLowerCase();

  const copy = useMemo(
    () => pickRandom(BRANDING_COPY[key] || DEFAULT_COPY),
    [key]
  );

  if (RESERVED.includes(key)) return <Navigate to="/" replace />;

  const LOCATIONS = [
    { key: "kamla-nagar", location: "Kamla Nagar", title: "Available in Kamla Nagar", area: "Kamla Nagar" },
    { key: "mukherjee-nagar", location: "Mukherjee Nagar", title: "Available in Mukherjee Nagar", area: "Mukherjee Nagar" },
    { key: "rajender-nagar", location: "Rajender Nagar", title: "Available in Rajender Nagar", area: "Rajender Nagar" },
  ];

  return (
    <main className="brand-page page apg-root">
      <BrandingHero copy={copy} />
      <WhyChoose />
      <HowItWorks />

      <div className="brand-stays-wrap">
        {LOCATIONS.map((loc) => (
          <StayRow 
            key={loc.key} 
            location={loc.location}
            title={loc.title}
            area={loc.area}
          />
        ))}
      </div>

      <OwnerBanner />
    </main>
  );
}